import React from 'react'

import { DateTime } from 'luxon'

import { SimpleTable, TableSkeleton, Alert } from '@/components/ui'
import { useApi, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceParam } from '@/insights/common/dataSourceParam'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { NonAlarmDateOptions } from '@/insights/components/CustomSingleSelect'
import LiveDuration from '@/insights/components/LiveDuration'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

interface DoorActivitiesParams extends InsightParametersType {
  companyId?: number
  assetId?: string
}

interface DoorActivities {
  assetId: number
  assetName: string
  closedDate: Date
  closedLatitude: number
  closedLongitude: number
  closedLocation: string
  companyId: number
  id: number
  monitoringPointName: string
  openedDate: Date
  openedLatitude: number
  openedLongitude: number
  openedLocation: string
}

const DoorActivities: InsightDefinition<DoorActivitiesParams, DoorActivities[]> = {
  active: true,
  slug: 'asset/door-activities',
  category: 'asset',
  name: 'Door Activities',
  description: 'List detailed door activities',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: NonAlarmDateOptions.map((d) => (d.value === 'today' ? { ...d, isDefault: true } : d)),
      },
    ],

    isValid: (configuration: DoorActivitiesParams) => {
      if (configuration.companyId && configuration.assetId) {
        return true
      }
      return 'Company and Asset are required.'
    },
  },

  load: async (api: ApiService, parameters: DoorActivitiesParams) => {
    const path = `/insights/asset/door-activities?${buildQuery(parameters)}`
    const response = await api.get<DoorActivities[]>({
      path,
      origin: 'DoorActivities',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: DoorActivitiesParams, title: string) => {
    const path = '/insights/asset/door-activities'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={16} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No activities found".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }

    const parameters = configuration.parameters as DoorActivitiesParams
    const format = 'dd-MMM-yyyy hh:mm a'
    const body = data
      .sort((a, b) => {
        const t = b.openedDate.getTime() - a.openedDate.getTime()
        if (t === 0) {
          return a.monitoringPointName.localeCompare(b.monitoringPointName)
        }
        return t
      })
      .map((sample) => ({
        'MP Name': sample.monitoringPointName,
        'Opened Date': sample.openedDate
          ? {
              id: `opened-${sample.id}`,
              date: sample.openedDate,
              display: DateTime.fromJSDate(sample.openedDate).toFormat(format),
            }
          : '',
        'Location Opened': genericLocationDrilldown({
          configuration,
          address: sample.openedLocation,
          latitude: sample.openedLatitude,
          longitude: sample.openedLongitude,
          companyId: parameters.companyId,
          markerFields: {
            Asset: sample.assetName,
            Time: sample.openedDate?.toLocaleString(),
          },
        }),
        'Closed Date': sample.closedDate
          ? {
              id: `closed-${sample.id}`,
              date: sample.closedDate,
              display: DateTime.fromJSDate(sample.closedDate).toFormat(format),
            }
          : '',
        'Location Closed': genericLocationDrilldown({
          configuration,
          address: sample.closedLocation,
          latitude: sample.closedLatitude,
          longitude: sample.closedLongitude,
          companyId: parameters.companyId,
          markerFields: {
            Asset: sample.assetName,
            Time: sample.closedDate?.toLocaleString(),
          },
        }),
        Duration: sample.openedDate
          ? {
              value: (sample.closedDate || new Date()).getTime() - sample.openedDate.getTime(),
              display: (
                <LiveDuration
                  raisedTime={sample.openedDate}
                  clearedTime={sample.closedDate}
                  className="block text-left min-w-fit"
                />
              ),
            }
          : '',
      }))

    const dataItem = body[0]
    const headers = dataItem
      ? Object.keys(dataItem)
      : Array(6)
          .fill(null)
          .map((_, index) => `placeholder-${index}`)

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as DoorActivitiesParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false${dataSourceParam(params)}`,
            origin: 'DoorActivities#caption',
          })
          if (response.data?.length) {
            const asset = response.data[0]
            setAsset(asset)
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default DoorActivities
